import React from 'react';
import { styled } from '@mui/material';
import InsideLoading from 'components/loading/InsideLoading';
import NoData from 'components/no-data/NoData';
import { plural, RecordMetric, RecordMetrics } from 'components/record-ficha/RecordMetric';
import RecordPriceList from 'components/record-ficha/RecordPriceList';
import RecordTimeline from 'components/record-ficha/RecordTimeline';
import { moneyFormat } from 'helpers/moneyFormat';
import history from 'services/history';
import { useFetchDoctorRecord } from './hooks/useFetchDoctorRecord';

const Content = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  minWidth: 0,
}));

interface DoctorRecordContentProps {
  id: string | undefined;
  reloadKey?: number;
}

const DoctorRecordContent: React.FC<DoctorRecordContentProps> = ({ id, reloadKey }) => {
  const { record, loading, error } = useFetchDoctorRecord(id, reloadKey);

  if (loading) return <InsideLoading />;

  if (error || !record) return <NoData message="Não foi possível carregar a ficha do médico." />;

  const { guides, budgets, procedures } = record;
  const closed = budgets.summary.closed;
  const open = budgets.summary.by_status?.open;
  const completed = guides.summary.completed;

  return (
    <Content>
      <RecordMetrics>
        <RecordMetric
          label="Orçamentos fechados"
          value={moneyFormat(closed.value_total)}
          helpers={[plural(closed.count, 'fechado', 'fechados')]}
        />
        <RecordMetric
          label="Orçamentos em aberto"
          value={moneyFormat(open?.value_total ?? 0)}
          helpers={[plural(open?.count ?? 0, 'orçamento', 'orçamentos')]}
        />
        <RecordMetric
          label="Guias concluídas"
          value={moneyFormat(completed.value_total)}
          helpers={[plural(completed.count, 'concluída', 'concluídas')]}
        />
      </RecordMetrics>

      <RecordPriceList
        title="Procedimentos que realiza"
        items={procedures.map(item => ({
          key: item.id,
          name: item.name || '-',
          min: item.sale_price_min,
          max: item.sale_price_max,
        }))}
        empty="Este médico ainda não está vinculado a nenhum procedimento. O vínculo e o preço são editados no cadastro do procedimento."
        note="Os preços são editados no cadastro do procedimento."
        onSelect={item => history.push(`/procedures/${procedures.find(p => p.id === item.key)?.procedure_id}`)}
      />

      <RecordTimeline budgets={budgets.items} guides={guides.items} counterpart="patient" subject="Este médico" />
    </Content>
  );
};

export default DoctorRecordContent;
